import { useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";
import { WaitlistForm } from "@/components/WaitlistForm";
import { Toaster } from "@/components/ui/sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  heroRadar:
    "https://images.unsplash.com/photo-1547190027-9156686aa2f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxjeWJlciUyMHNlY3VyaXR5JTIwbWF0cml4JTIwY29kZXxlbnwwfHx8fDE3OTIwNzU2ODV8MA&ixlib=rb-4.1.0&q=85",
  slackAlert:
    "https://images.unsplash.com/photo-1663408319620-3551f425c81e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxub3RpZmljYXRpb24lMjBhbGVydCUyMHNjcmVlbiUyMGRpZ2l0YWx8ZW58MHx8fHwxNzkyMDc1Njg1fDA&ixlib=rb-4.1.0&q=85",
  cveTracker:
    "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxkYXRhJTIwdmlzdWFsaXphdGlvbiUyMHRocmVhdHxlbnwwfHx8fDE3OTIwNzU2ODV8MA&ixlib=rb-4.1.0&q=85",
  tierMatrix:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbiUyMHRocmVhdHxlbnwwfHx8fDE3OTIwNzU2ODV8MA&ixlib=rb-4.1.0&q=85",
};

const Nav = () => (
  <header
    data-testid="nav-header"
    className="border-b border-[#222] bg-[#050505]/95 sticky top-0 z-50 backdrop-blur-sm"
  >
    <nav className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between text-xs">
      <a
        data-testid="nav-brand"
        href="#top"
        className="flex items-center gap-2 text-white font-bold tracking-tight"
      >
        <svg className="w-4 h-4 text-[#ff3366]" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
          <path d="M12 7v5l3 3" />
        </svg>
        <span>OPENBREACH//SEC</span>
      </a>
      <div className="flex items-center gap-6">
        <span className="text-[#666] hidden sm:inline">[STATUS: RECON_ENGINE_ONLINE]</span>
        <a
          data-testid="nav-cta-deploy-demo"
          href="#join"
          className="px-3 py-1.5 bg-[#ff3366] text-black font-bold uppercase tracking-widest text-[11px] hover:bg-[#ff5588] transition-colors"
        >
          Deploy Demo
        </a>
      </div>
    </nav>
  </header>
);

const Hero = () => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => setCount(res.data.count))
      .catch(() => setCount(null));
  }, []);

  return (
    <section
      data-testid="hero-section"
      id="top"
      className="py-20 border-b border-[#222] relative radar-grid"
    >
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-block text-[11px] text-[#ff3366] font-bold tracking-widest uppercase mb-4 border-l-2 border-[#ff3366] pl-2">
            Autonomous SecOps Agent
          </div>
          <h1
            data-testid="hero-headline"
            className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-6 uppercase leading-tight"
          >
            <span className="hero-ln">
              <span>Autonomous AI patches</span>
            </span>
            <span className="hero-ln">
              <span className="text-[#ff3366]">critical security flaws.</span>
            </span>
          </h1>
          <p className="text-xs sm:text-sm text-[#888] leading-relaxed mb-8 max-w-lg">
            Always-on AI that finds AND fixes vulnerabilities, not just reports them.
          </p>

          <div id="join">
            <WaitlistForm />
          </div>
          <p className="text-[10px] text-[#555] uppercase tracking-wider">
            Zero telemetry retained &bull; Online everywhere
            {count !== null && (
              <span data-testid="waitlist-count" className="text-[#ff3366]">
                {" "}
                &bull; QUEUE_LENGTH: {count}
              </span>
            )}
          </p>
        </div>

        <div className="lg:col-span-5">
          <div
            data-testid="hero-radar-card"
            className="relative border border-[#333] bg-[#0c0c0c] p-4 card-drift overflow-hidden"
          >
            <img
              src={IMAGES.heroRadar}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
            />
            <div className="relative">
              <div className="flex justify-between items-center text-[10px] text-[#666] border-b border-[#222] pb-2 mb-3">
                <span>RADAR_SCAN // RUNTIME_MONITOR</span>
                <span className="text-[#ff3366] animate-pulse">&#9679; LIVE</span>
              </div>
              <div className="space-y-2 text-[11px]">
                <div className="flex justify-between text-[#aaa]">
                  <span>THREAT_LEVEL:</span>
                  <span className="text-red-400">HIGH (CVE-2025-1102)</span>
                </div>
                <div className="flex justify-between text-[#aaa]">
                  <span>TARGET_HOST:</span>
                  <span className="text-white">api-gateway.prod</span>
                </div>
                <div className="flex justify-between text-[#aaa]">
                  <span>ACTION:</span>
                  <span className="text-[#ff3366]">AUTO_PR_DISPATCHED</span>
                </div>
                <div className="p-2 bg-[#161616]/90 border border-[#2a2a2a] text-[10px] text-[#888] mt-3">
                  [+] AST rewrite completed in 420ms
                  <br />
                  [+] Regression harness: 0 failures
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div data-testid="intel-marquee" className="border-b border-[#222] py-4 bg-[#080808] overflow-hidden">
    <div className="mq-track text-xs text-[#555] font-bold tracking-wider">
      <span>
        INTELLIGENCE FEED &bull; CODEBASE SCANNING &bull; SUPPLY CHAIN DEFENSE &bull; SECRET
        RECOVERY &bull; LIVE PENTEST &bull;&nbsp;
      </span>
      <span>
        INTELLIGENCE FEED &bull; CODEBASE SCANNING &bull; SUPPLY CHAIN DEFENSE &bull; SECRET
        RECOVERY &bull; LIVE PENTEST &bull;&nbsp;
      </span>
    </div>
  </div>
);

const PIPELINES = [
  {
    tag: "[01] SCAN",
    title: "Codebase Scanning",
    body: "Semantic graph parsing locating zero-day exploits before commit merge.",
  },
  {
    tag: "[02] GUARD",
    title: "Supply Chain Vetting",
    body: "Lockfile provenance checking against verified cryptographic registries.",
  },
  {
    tag: "[03] REVOKE",
    title: "Secret Rotation",
    body: "Immediate discovery and automated token invalidation at edge gateways.",
  },
  {
    tag: "[04] PROBE",
    title: "AI Pentesting",
    body: "Adversarial agent red-teaming against staging environments continuously.",
  },
];

const ValueProps = () => (
  <section data-testid="pipelines-section" className="py-20 border-b border-[#222] max-w-6xl mx-auto px-6">
    <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-8 flex items-center gap-2">
      <span className="w-2 h-2 bg-[#ff3366]"></span> CORE_PIPELINES
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {PIPELINES.map((p, i) => (
        <div
          key={p.tag}
          data-testid={`pipeline-card-${i + 1}`}
          className="p-4 border border-[#222] bg-[#090909] hover:border-[#ff3366]/50 transition-colors"
        >
          <div className="text-[10px] text-[#ff3366] font-bold mb-1">{p.tag}</div>
          <h3 className="text-xs font-bold text-white uppercase mb-2">{p.title}</h3>
          <p className="text-[11px] text-[#777] leading-relaxed">{p.body}</p>
        </div>
      ))}
    </div>
  </section>
);

const BENCHMARKS = [
  {
    img: IMAGES.slackAlert,
    title: "Slack & Linear Link",
    body: "Automated triage tickets created with reproduction steps.",
  },
  {
    img: IMAGES.cveTracker,
    title: "Zero-Day Radar",
    body: "Proactive intelligence mapped straight to internal modules.",
  },
  {
    img: IMAGES.tierMatrix,
    title: "Startup \u2022 Enterprise",
    body: "Self-hosted execution with zero config latency.",
  },
];

const Benchmarks = () => (
  <section data-testid="benchmarks-section" className="py-20 border-b border-[#222] max-w-6xl mx-auto px-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {BENCHMARKS.map((b, i) => (
        <div
          key={b.title}
          data-testid={`benchmark-card-${i + 1}`}
          className="border border-[#222] bg-[#0a0a0a] overflow-hidden hover:border-[#ff3366]/50 transition-colors group"
        >
          <div className="h-32 overflow-hidden border-b border-[#222]">
            <img
              src={b.img}
              alt={b.title}
              loading="lazy"
              className="w-full h-full object-cover opacity-60 grayscale group-hover:opacity-90 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            />
          </div>
          <div className="p-5">
            <h3 className="text-xs font-bold text-white uppercase mb-2">{b.title}</h3>
            <p className="text-[11px] text-[#777]">{b.body}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const CLIENTS = [
  { name: "NEXAFIN", glyph: "M12 2 L22 12 L12 22 L2 12 Z" },
  { name: "VOLTSTACK", glyph: "M12 3 L21 21 L3 21 Z" },
  { name: "KERNLABS", glyph: "M12 2 L21 7 L21 17 L12 22 L3 17 L3 7 Z" },
  { name: "DRIFTLY", glyph: "M4 4 H20 V20 H4 Z" },
  { name: "HELIXPAY", glyph: "M12 4 A8 8 0 1 1 4 12" },
];

const TrustedBy = () => (
  <section data-testid="trusted-by-section" className="py-12 border-b border-[#222] max-w-6xl mx-auto px-6">
    <p className="text-[10px] text-[#555] uppercase tracking-widest mb-6 text-center">
      TRUSTED_BY // ENGINEERING TEAMS SHIPPING FAST
    </p>
    <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
      {CLIENTS.map((c) => (
        <div
          key={c.name}
          data-testid={`client-logo-${c.name.toLowerCase()}`}
          className="flex items-center gap-2 text-[#666] hover:text-[#ededed] transition-colors"
        >
          <svg className="w-4 h-4 text-[#ff3366]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d={c.glyph} />
          </svg>
          <span className="text-xs font-bold tracking-widest">{c.name}</span>
        </div>
      ))}
    </div>
  </section>
);

const FEATURES = [
  {
    id: "codebase",
    label: "CODEBASE_SCANNING // PR_REVIEW",
    status: "FIX_PR_READY",
    body: (
      <>
        <div className="text-[#777]">102&nbsp;&nbsp;const handler = async (req, res) =&gt; {"{"}</div>
        <div className="text-red-400">-&nbsp;&nbsp;&nbsp;const session = getServerSession(req, res);</div>
        <div className="text-[#ff3366]">+&nbsp;&nbsp;&nbsp;const session = await getServerSession(req, res);</div>
        <div className="text-[#777]">105&nbsp;&nbsp;&nbsp;&nbsp;if (!session) return res.status(401);</div>
      </>
    ),
    foot: "MISSING_AWAIT // AUTH_BYPASS · pages/api/file/[...file].ts",
  },
  {
    id: "supplychain",
    label: "SUPPLY_CHAIN_DEFENSE // LOCKFILE_AUDIT",
    status: "1 PACKAGE QUARANTINED",
    body: (
      <>
        {[
          ["next-auth", "Safe", false],
          ["react", "Safe", false],
          ["chalk-core", "TYPOSQUAT", true],
          ["zod", "Safe", false],
        ].map(([pkg, st, bad]) => (
          <div key={pkg} className="flex justify-between">
            <span className="text-[#aaa]">{pkg}</span>
            <span className={bad ? "text-[#ff3366] font-bold" : "text-[#666]"}>{st}</span>
          </div>
        ))}
      </>
    ),
    foot: "REGISTRY_PROVENANCE_MISMATCH · install blocked pre-merge",
  },
  {
    id: "secrets",
    label: "SECRET_RECOVERY // ENV_SCAN",
    status: "3 SECRETS ROTATED",
    body: (
      <>
        <div className="text-[#777]">.env.production — lines 14–16</div>
        <div className="flex justify-between"><span className="text-[#aaa]">STRIPE_SECRET_KEY</span><span className="text-[#ff3366]">sk_live_•••[REDACTED]</span></div>
        <div className="flex justify-between"><span className="text-[#aaa]">GITHUB_TOKEN</span><span className="text-[#ff3366]">ghp_•••[REDACTED]</span></div>
        <div className="flex justify-between"><span className="text-[#aaa]">AWS_SECRET_ACCESS_KEY</span><span className="text-[#ff3366]">•••[REDACTED]</span></div>
      </>
    ),
    foot: "AUTO_ROTATION dispatched at edge gateways",
  },
  {
    id: "pentest",
    label: "LIVE_PENTEST // ACCESS_CONTROL",
    status: "IDOR CONFIRMED",
    body: (
      <>
        <div className="text-[#888]">$ curl -i "$TARGET/api/invoices/1042" -H "Authorization: Bearer $USER_248"</div>
        <div className="text-white mt-1">HTTP/1.1 200 OK</div>
        <div className="text-[#777]">{"{ \"id\": \"inv_1042\", \"owner\": \"user_901\" }"}</div>
        <div className="text-[#ff3366] mt-1">Cross-account access · expected 403</div>
      </>
    ),
    foot: "EXPLOIT_VERIFIED in sandbox before reporting",
  },
];

const ProductShowcase = () => (
  <section data-testid="product-showcase-section" className="py-20 border-b border-[#222] max-w-6xl mx-auto px-6">
    <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
      <span className="w-2 h-2 bg-[#ff3366]"></span> PRODUCT_SURFACE
    </h2>
    <p className="text-[11px] text-[#777] mb-8 max-w-xl">
      The most context-aware AI security engineer that works 24/7 — find, verify, and fix real vulnerabilities using AI.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {FEATURES.map((f) => (
        <div
          key={f.id}
          data-testid={`feature-card-${f.id}`}
          className="border border-[#222] bg-[#090909] hover:border-[#ff3366]/50 transition-colors"
        >
          <div className="flex justify-between items-center text-[10px] border-b border-[#222] px-4 py-2">
            <span className="text-[#666]">{f.label}</span>
            <span className="text-[#ff3366] font-bold">{f.status}</span>
          </div>
          <div className="p-4 text-[11px] leading-relaxed space-y-1 bg-[#0c0c0c]">{f.body}</div>
          <div className="px-4 py-2 text-[10px] text-[#555] border-t border-[#222]">{f.foot}</div>
        </div>
      ))}
    </div>
  </section>
);

const INTEGRATIONS = ["SLACK", "LINEAR", "GITHUB", "GITLAB", "JIRA", "DATADOG"];

const Integrations = () => (
  <section data-testid="integrations-section" className="py-16 border-b border-[#222] max-w-6xl mx-auto px-6">
    <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
      <span className="w-2 h-2 bg-[#ff3366]"></span> INTEGRATION_MESH
    </h2>
    <p className="text-[11px] text-[#777] mb-8 max-w-xl">
      Assign it to openbreach in your tracker, get a fix back as a pull request — right where your team works.
    </p>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {INTEGRATIONS.map((name) => (
        <div
          key={name}
          data-testid={`integration-${name.toLowerCase()}`}
          className="border border-[#222] bg-[#090909] px-4 py-3 text-center text-[11px] font-bold tracking-widest text-[#888] hover:text-white hover:border-[#ff3366]/50 transition-colors"
        >
          {name}
        </div>
      ))}
    </div>
  </section>
);

const TESTIMONIALS = [
  {
    quote:
      "Openbreach shipped a fix PR before our standup even started. It caught an auth bypass our previous scanner never flagged.",
    name: "MAYA CHEN",
    role: "CTO // VOLTSTACK",
  },
  {
    quote:
      "We went from annual pentests to continuous coverage in a week. The queue stays actionable because nothing it reports is noise.",
    name: "DANIEL OKAFOR",
    role: "HEAD OF ENGINEERING // NEXAFIN",
  },
  {
    quote:
      "It's the first security tool our developers actually open. Fixes land as pull requests, not tickets that rot in a backlog.",
    name: "SOFIA RAMIREZ",
    role: "VP PLATFORM // HELIXPAY",
  },
];

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-20 border-b border-[#222] max-w-6xl mx-auto px-6">
    <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-8 flex items-center gap-2">
      <span className="w-2 h-2 bg-[#ff3366]"></span> FIELD_REPORTS
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {TESTIMONIALS.map((t, i) => (
        <figure
          key={t.name}
          data-testid={`testimonial-card-${i + 1}`}
          className="border border-[#222] bg-[#0a0a0a] p-5 flex flex-col justify-between hover:border-[#ff3366]/50 transition-colors"
        >
          <blockquote className="text-[11px] text-[#aaa] leading-relaxed mb-6">
            <span className="text-[#ff3366] font-bold">&gt;&nbsp;</span>"{t.quote}"
          </blockquote>
          <figcaption>
            <div className="text-[11px] font-bold text-white">{t.name}</div>
            <div className="text-[10px] text-[#666] tracking-widest">{t.role}</div>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const TIERS = [
  {
    id: "startups",
    tag: "TIER_01",
    title: "For Startups",
    body: "Ship fast without making security a second job. Find and fix vulnerabilities, run pentests, and get ready for enterprise security reviews.",
  },
  {
    id: "enterprise",
    tag: "TIER_02",
    title: "For Enterprises",
    body: "Bring code scanning, pentesting, and vulnerability management into one workflow. Prioritize real business risk and give every team a clear path to remediation.",
  },
];

const Tiers = () => (
  <section data-testid="tiers-section" className="py-20 border-b border-[#222] max-w-6xl mx-auto px-6">
    <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-8 flex items-center gap-2">
      <span className="w-2 h-2 bg-[#ff3366]"></span> DEPLOYMENT_TIERS
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {TIERS.map((t) => (
        <div
          key={t.id}
          data-testid={`tier-card-${t.id}`}
          className="border border-[#222] bg-[#090909] p-6 hover:border-[#ff3366]/50 transition-colors"
        >
          <div className="text-[10px] text-[#ff3366] font-bold mb-2">[{t.tag}]</div>
          <h3 className="text-sm font-bold text-white uppercase mb-3">{t.title}</h3>
          <p className="text-[11px] text-[#777] leading-relaxed mb-5">{t.body}</p>
          <a
            data-testid={`tier-cta-${t.id}`}
            href="#join"
            className="text-[11px] text-[#ff3366] font-bold uppercase tracking-widest hover:underline"
          >
            Request access &rarr;
          </a>
        </div>
      ))}
    </div>
  </section>
);

const FAQ_ITEMS = [
  {
    q: "What is openbreach?",
    a: "An always-on AI security engineer that finds AND fixes vulnerabilities, not just reports them. It scans code, probes live environments, and dispatches fix pull requests.",
  },
  {
    q: "Does openbreach access proprietary source code?",
    a: "Processing occurs strictly in ephemeral runner sandboxes.",
  },
  {
    q: "How fast does autonomous remediation run?",
    a: "Fix proposals arrive in less than two minutes from commit push.",
  },
  {
    q: "Who is openbreach built for?",
    a: "Startup engineering teams shipping fast that cannot make security a second job, online everywhere.",
  },
];

const Faq = () => (
  <section data-testid="faq-section" className="py-16 border-b border-[#222] max-w-3xl mx-auto px-6">
    <h2 className="text-xs font-bold text-white uppercase tracking-widest mb-6">
      [FAQ: SYSTEM_SPECS]
    </h2>
    <Accordion type="single" collapsible className="text-xs">
      {FAQ_ITEMS.map((item, i) => (
        <AccordionItem
          key={item.q}
          value={`item-${i}`}
          data-testid={`faq-item-${i + 1}`}
          className="border-b border-[#222]"
        >
          <AccordionTrigger
            data-testid={`faq-trigger-${i + 1}`}
            className="text-white font-bold text-xs hover:text-[#ff3366] hover:no-underline py-3 text-left"
          >
            Q: {item.q}
          </AccordionTrigger>
          <AccordionContent data-testid={`faq-content-${i + 1}`} className="text-[#777] pb-3">
            A: {item.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const FooterCta = () => (
  <section data-testid="footer-cta-section" className="py-20 border-b border-[#222] radar-grid">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-3">
        Request <span className="text-[#ff3366]">demo access.</span>
      </h2>
      <p className="text-xs text-[#777] mb-8">
        Join the waitlist. Demo slots are dispatched to operators in queue order.
      </p>
      <div className="flex justify-center">
        <WaitlistForm compact />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="footer"
    className="py-8 bg-[#020202] text-[10px] text-[#555] uppercase tracking-wider"
  >
    <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
      <div>OPENBREACH // ONLINE EVERYWHERE</div>
      <div>&copy; 2025 OPENBREACH AGENT SYSTEMS</div>
      <a data-testid="footer-top-link" href="#top" className="text-[#ff3366] hover:underline">
        TOP &uarr;
      </a>
    </div>
  </footer>
);

export default function App() {
  return (
    <div className="font-terminal bg-[#050505] text-[#ededed] antialiased min-h-screen">
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <TrustedBy />
        <ValueProps />
        <ProductShowcase />
        <Benchmarks />
        <Integrations />
        <Testimonials />
        <Tiers />
        <Faq />
        <FooterCta />
      </main>
      <Footer />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
